import { useEffect } from "react";

/**
 * Allows vertical drag gestures on fixed/non-scrollable elements
 * (e.g. sticky headers/footers) to scroll a target container.
 */
export const useVerticalDragScroll = ({ sourceRefs = [], scrollTargetRef }) => {
  useEffect(() => {
    const target = scrollTargetRef?.current;
    if (!target || !sourceRefs.length) return;

    let startY = 0;
    let startX = 0;
    let lastY = 0;
    let isVerticalGesture = false;
    let hasMovedEnough = false;
    let queuedDeltaY = 0;
    let rafId = null;

    const flushScroll = () => {
      rafId = null;
      if (queuedDeltaY === 0) return;
      target.scrollTop -= queuedDeltaY;
      queuedDeltaY = 0;
    };

    const queueScroll = (deltaY) => {
      queuedDeltaY += deltaY;
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(flushScroll);
    };

    const resetState = () => {
      isVerticalGesture = false;
      hasMovedEnough = false;
      startY = 0;
      startX = 0;
      lastY = 0;
      queuedDeltaY = 0;
    };

    const handleTouchStart = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      startY = touch.clientY;
      startX = touch.clientX;
      lastY = touch.clientY;
      isVerticalGesture = false;
      hasMovedEnough = false;
    };

    const handleTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaFromStartY = touch.clientY - startY;
      const deltaFromStartX = touch.clientX - startX;
      const moveDistance = Math.max(
        Math.abs(deltaFromStartY),
        Math.abs(deltaFromStartX)
      );

      if (!hasMovedEnough && moveDistance > 6) {
        hasMovedEnough = true;
        isVerticalGesture = Math.abs(deltaFromStartY) > Math.abs(deltaFromStartX);
      }

      if (!hasMovedEnough || !isVerticalGesture) return;

      const deltaY = touch.clientY - lastY;
      queueScroll(deltaY);
      lastY = touch.clientY;
      event.preventDefault();
    };

    const handleTouchEnd = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      flushScroll();
      resetState();
    };

    const cleanups = sourceRefs
      .map((ref) => ref?.current)
      .filter(Boolean)
      .map((node) => {
        node.addEventListener("touchstart", handleTouchStart, { passive: true });
        node.addEventListener("touchmove", handleTouchMove, { passive: false });
        node.addEventListener("touchend", handleTouchEnd, { passive: true });
        node.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
          node.removeEventListener("touchstart", handleTouchStart);
          node.removeEventListener("touchmove", handleTouchMove);
          node.removeEventListener("touchend", handleTouchEnd);
          node.removeEventListener("touchcancel", handleTouchEnd);
        };
      });

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [scrollTargetRef, sourceRefs]);
};

export default useVerticalDragScroll;
